import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Caret } from '@/shared/components/ui/Caret'
import { createPortal } from 'react-dom';
import { useT } from '@/shared/i18n/useT';
import type { GridDownloadActions } from './gridExportRegistry';

interface CopySplitButtonProps {
  onCopy: () => void;
  copyHeaders: boolean;
  onToggleCopyHeaders: (value: boolean) => void;
  copyNumberFormat: boolean;
  onToggleCopyNumberFormat: (value: boolean) => void;
  copyFormulas: boolean;
  onToggleCopyFormulas: (value: boolean) => void;
  /** Extra checkboxes, each shown only when its row kind is on screen. */
  extraOptions?: Array<{ key: string; label: string; checked: boolean; onToggle: (value: boolean) => void }>;
  /** The Download submenu, listed below the copy actions. */
  onDownload?: GridDownloadActions;
  hasSelection?: boolean;
  onCopySelection?: (asMarkdown?: boolean) => void;
  onCopyAll?: (asMarkdown?: boolean) => void;
  disabled?: boolean;
  /** Only the options caret, for a bar that draws its own copy button (the phone selection bar). */
  caretOnly?: boolean;
}

export function CopySplitButton({
  onCopy,
  copyHeaders,
  onToggleCopyHeaders,
  copyNumberFormat,
  onToggleCopyNumberFormat,
  copyFormulas,
  onToggleCopyFormulas,
  extraOptions = [],
  onDownload,
  hasSelection = false,
  onCopySelection,
  onCopyAll,
  disabled = false,
  caretOnly = false,
}: CopySplitButtonProps) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ left: 8, top: 8 });
  const [downloadAt, setDownloadAt] = useState<{ left: number; top: number } | null>(null);
  const closeDownloadTimer = useRef<number | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const copyLabel = hasSelection ? t('grid.copySelection') : t('grid.copyAll');
  const menuLabel = t('grid.copyMenu');

  useEffect(() => {
    if (!open) return;
    const close = (event: MouseEvent) => {
      const target = event.target as Node;
      if (!wrapperRef.current?.contains(target) && !menuRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) return;
    const updatePosition = () => {
      const rect = wrapperRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 200;
      setMenuPosition({
        left: Math.min(Math.max(8, rect.left), Math.max(8, window.innerWidth - menuWidth - 8)),
        top: rect.bottom + 4,
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  useEffect(() => { if (!open) setDownloadAt(null); }, [open]);

  const openDownload = (target: HTMLElement) => {
    if (closeDownloadTimer.current !== null) window.clearTimeout(closeDownloadTimer.current);
    const rect = target.getBoundingClientRect();
    const width = 224;
    const left = rect.right + 4 + width > window.innerWidth - 8 ? Math.max(8, rect.left - width - 4) : rect.right + 4;
    setDownloadAt({ left, top: Math.max(8, Math.min(rect.top - 4, window.innerHeight - 300)) });
  };
  const closeDownloadSoon = () => {
    if (closeDownloadTimer.current !== null) window.clearTimeout(closeDownloadTimer.current);
    closeDownloadTimer.current = window.setTimeout(() => setDownloadAt(null), 150);
  };
  const keepDownload = () => {
    if (closeDownloadTimer.current !== null) window.clearTimeout(closeDownloadTimer.current);
  };
  const runDownload = (run: () => void | Promise<void>) => {
    setOpen(false);
    void run();
  };

  const handleCopySelection = (asMarkdown = false) => {
    setOpen(false);
    if (onCopySelection) onCopySelection(asMarkdown);
    else onCopy();
  };

  const handleCopyAll = (asMarkdown = false) => {
    setOpen(false);
    if (onCopyAll) onCopyAll(asMarkdown);
    else onCopy();
  };

  return (
    <div
      ref={wrapperRef}
      className={`relative inline-flex ${caretOnly ? 'h-10' : 'h-7'} shrink-0 overflow-hidden rounded text-fg`}
    >
      {!caretOnly && <button
        type="button"
        aria-label={copyLabel}
        title={copyLabel}
        disabled={disabled}
        onClick={onCopy}
        className="flex h-7 items-center justify-center pl-1.5 pr-0.5 transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <rect x="9" y="9" width="13" height="13" rx="2" />
          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
        </svg>
      </button>}
      <button
        type="button"
        aria-label={menuLabel}
        title={menuLabel}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((prev) => !prev)}
        className={`flex ${caretOnly ? 'h-10 w-5 justify-center rounded-lg text-text-muted' : 'h-7 w-4 justify-start'} items-center transition-colors hover:bg-surface-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus disabled:cursor-default disabled:opacity-40`}
      >
        <Caret />
      </button>
      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            aria-label={menuLabel}
            style={{ position: 'fixed', left: menuPosition.left, top: menuPosition.top }}
            className="z-50 min-w-48 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-border bg-surface p-1 text-xs shadow-xl flex flex-col gap-0.5"
          >
            {hasSelection && (
              <button
                type="button"
                role="menuitem"
                onClick={() => handleCopySelection(false)}
                className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none"
              >
                <span className="text-xs font-normal text-fg">{t('grid.copySelection')}</span>
              </button>
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => handleCopyAll(false)}
              className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none"
            >
              <span className="text-xs font-normal text-fg">{t('grid.copyAll')}</span>
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => handleCopyAll(true)}
              className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none"
            >
              <span className="text-xs font-normal text-fg">{t('grid.copyAllMd')}</span>
            </button>
            {onDownload && (
              <button
                type="button"
                role="menuitem"
                aria-haspopup="menu"
                aria-expanded={downloadAt !== null}
                onMouseEnter={(e) => openDownload(e.currentTarget)}
                onMouseLeave={closeDownloadSoon}
                onFocus={(e) => openDownload(e.currentTarget)}
                onClick={(e) => openDownload(e.currentTarget)}
                className={`flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 ${downloadAt ? 'bg-surface-3' : ''} focus-visible:bg-surface-3 focus-visible:outline-none`}
              >
                <span className="text-xs font-normal text-fg">{t('grid.download.label')}</span>
                <svg className="ml-auto h-3 w-3 text-text-muted" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m9 18 6-6-6-6" /></svg>
              </button>
            )}
            {onDownload && downloadAt && (
              <div
                role="menu"
                aria-label={t('grid.download.label')}
                onMouseEnter={keepDownload}
                onMouseLeave={closeDownloadSoon}
                style={{ position: 'fixed', left: downloadAt.left, top: downloadAt.top }}
                className="z-50 w-56 max-w-[calc(100vw-1rem)] rounded-xl border border-border bg-surface p-1 text-xs shadow-xl flex flex-col gap-0.5"
              >
                <div className="px-2.5 pb-0.5 pt-1 text-xs font-medium text-text-muted">{t('grid.download.thisSheet')}</div>
                {(['xlsx', 'csv', 'md', 'tsv', 'txt'] as const).map((format) => (
                  <button key={format} type="button" role="menuitem" onClick={() => runDownload(() => onDownload.sheet(format))} className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none">
                    <span className="text-xs font-normal text-fg">{t(`grid.download.${format}`)}</span>
                  </button>
                ))}
                <div className="my-1 border-t border-border" />
                <div className="px-2.5 pb-0.5 pt-1 text-xs font-medium text-text-muted">{t('grid.download.wholeFile')}</div>
                {(['xlsx', 'md'] as const).map((format) => (
                  <button key={format} type="button" role="menuitem" onClick={() => runDownload(() => onDownload.file(format))} className="flex w-full cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3 focus-visible:bg-surface-3 focus-visible:outline-none">
                    <span className="text-xs font-normal text-fg">{t(`grid.download.${format}`)}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="my-1 border-t border-border" />
            <div className="px-2.5 pb-0.5 pt-1 text-xs font-medium text-text-muted">{t('grid.copyInclude')}</div>
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3">
              <input
                type="checkbox"
                checked={copyHeaders}
                onChange={(e) => onToggleCopyHeaders(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-brand focus:ring-1 focus:ring-focus"
              />
              <span className="text-xs font-normal text-fg">{t('grid.copyHeaders')}</span>
            </label>
            <label className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3">
              <input
                type="checkbox"
                checked={copyNumberFormat}
                onChange={(e) => onToggleCopyNumberFormat(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-border accent-brand focus:ring-1 focus:ring-focus"
              />
              <span className="text-xs font-normal text-fg">{t('grid.copyNumberFormat')}</span>
            </label>
            {extraOptions.map((option) => (
              <label key={option.key} className="flex cursor-pointer select-none items-center gap-2 rounded-lg px-2.5 py-1.5 text-left text-fg transition-colors hover:bg-surface-3">
                <input
                  type="checkbox"
                  checked={option.checked}
                  onChange={(e) => option.onToggle(e.target.checked)}
                  className="h-3.5 w-3.5 rounded border-border accent-brand focus:ring-1 focus:ring-focus"
                />
                <span className="text-xs font-normal text-fg">{option.label}</span>
              </label>
            ))}
            <div
              role="radiogroup"
              aria-label={t('grid.copyFormulasLabel')}
              className="flex items-center gap-2 px-2.5 py-1.5"
            >
              <span className="text-xs font-normal text-fg">{t('grid.copyFormulasLabel')}</span>
              <div className="ml-auto flex rounded-full border border-border p-0.5">
                {([true, false] as const).map((formulas) => {
                  const selected = copyFormulas === formulas;
                  return (
                    <button
                      key={String(formulas)}
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      onClick={() => onToggleCopyFormulas(formulas)}
                      className={[
                        'rounded-full px-2 py-0.5 text-xs transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus',
                        selected ? 'bg-accent-soft font-medium text-fg' : 'text-muted hover:text-fg',
                      ].join(' ')}
                    >
                      {formulas ? t('grid.copyFormulas') : t('grid.copyValues')}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
